import { useEffect } from 'react';
import { useLocale } from 'next-intl';
import { usePaletteStore, paletteSelectors } from '../model/palette.store';
import { usePaletteOperations } from './use-palette-operations';

/**
 * Hook to fetch and use palettes from the API
 * Automatically fetches palettes on mount if not already loaded
 */
export function usePalettes() {
  const locale = useLocale();

  // Use palette operations from feature layer
  const operations = usePaletteOperations();
  const { fetchPalettes, getPaletteById, getPaletteColors, reset } = operations;

  // Get state from store
  const palettes = usePaletteStore(paletteSelectors.sortedPalettes);
  const isLoading = usePaletteStore(paletteSelectors.isLoading);
  const error = usePaletteStore(paletteSelectors.error);
  const isInitialized = usePaletteStore(paletteSelectors.isInitialized);
  const cachedLocale = usePaletteStore((state) => state.locale);

  useEffect(() => {
    // Reset if locale changed
    if (isInitialized && cachedLocale !== locale) {
      reset();
    }
  }, [locale, isInitialized, cachedLocale, reset]);

  useEffect(() => {
    // Fetch palettes on mount if not already initialized
    if (!isInitialized && !isLoading) {
      fetchPalettes();
    }
  }, [fetchPalettes, isInitialized, isLoading]);

  return {
    palettes,
    isLoading,
    error,
    isInitialized,
    getPaletteById,
    getPaletteColors,
    refetch: fetchPalettes,
  };
}
